import React, { useEffect, useState } from 'react';
import useBoardContentWidget from '../../hooks/useBoardContentWidget';
import { parseInt } from 'lodash';
import { pinWidget, unpinWidget, unpinWidgetFromBoard } from '../../../services/homeService';

import { WidgetWrapperProps } from './widgetWrapperProps';
import { ICurrency } from '../../types/ICurrency';

import Icon from '../../../ui/icon/icon';
import { Link, useParams } from 'react-router-dom';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import NotesPopup from '../../../ui/popup/notesPopup/notesPopup';
import { MODAL, POPUP } from '../../../../redux/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';

import './widgetWrapper.scss';

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
                                                       widget,
                                                       widgetCurrencies,
                                                       localCurrency,
                                                       handleLocalCurrencyChange,
                                                       notes,
                                                       selectedObject,
                                                       selectedCurrency,
                                                       moveWidget,
                                                       removeWidget,
                                                       customisation,
                                                       handleLink,
                                                       renderIcons,
                                                       children,
                                                       setWidgetExpand,
                                                       homeContent,
                                                       widgetView,
                                                       handleUnpin,
                                                       handleUnpinFromBoardCallback,
                                                       handlePinCallback,
                                                       unpinId,
                                                     }) => {
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [expand, setExpand] = useState<boolean>(false);
  const [pinned, setPinned] = useState<boolean>(false);

  const impersonate = useSelector((state: RootState) => state.impersonate.impersonate);
  const dispatch = useDispatch();
  const params = useParams();

  const { widgetSetup } = useBoardContentWidget();

  const currencySelection = widgetSetup(widget).currencySelection || homeContent;

  const onExpand = (): void => {
    setExpand(true);
  };

  const onCollapse = (): void => {
    setExpand(false);
  };

  const handlePinWidget = () => {
    const data = {
      contentType: 'analysisContent',
      widgetId: widget.id,
      objectId: selectedObject.objectId,
      objectPath: selectedObject.objectPath,
      nodes: [],
    };
    pinWidget(parseInt(params.investorId), parseInt(params.boardId), data).then(() => {
      handlePinCallback({
        investorObjectId: parseInt(params.investorId),
        objectPath: selectedObject.objectPath,
        widgetId: widget.id,
      });
    }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    setPinned(true);
  };

  const handleUnpinWidget = () => {
    unpinWidget(unpinId).then(() => {
      handleUnpin();
    }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    setPinned(false);
  };

  const handleUnpinFromBoard = () => {
    unpinWidgetFromBoard(parseInt(params.investorId), widget.id, selectedObject.objectPath).then(() => {
      handleUnpinFromBoardCallback({
        ...widget,
        investorObjectId: parseInt(params.investorId),
        objectPath: selectedObject.objectPath,
        widgetId: widget.id,
      });
    }).catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }));
    setPinned(false);
  };

  const renderPinIcon = () => {
    if (homeContent) {
      return <Icon
        onClick={impersonate ? () => dispatch({ type: MODAL.SWITCH_IMPERSONATE_MODE_MODAL }) : handleUnpinWidget}
        name={'fas fa-thumbtack'}
        className={'pointer export-icon hidden-mobile pinned-icon widget-icon'} />;
    } else if (!pinned) {
      return <Icon
        onClick={impersonate ? () => dispatch({ type: MODAL.SWITCH_IMPERSONATE_MODE_MODAL }) : handlePinWidget}
        name={'far fa-thumbtack'}
        className={'pointer export-icon hidden-mobile widget-icon'} />;
    } else {
      return <Icon
        onClick={impersonate ? () => dispatch({ type: MODAL.SWITCH_IMPERSONATE_MODE_MODAL }) : handleUnpinFromBoard}
        name={'fas fa-thumbtack'}
        className={'pointer export-icon hidden-mobile pinned-icon widget-icon '} />;
    }
  };

  const isPinned = (widget, object) => {
    let objectMatch = false;
    let investorMatch = false;
    let widgetMatch = false;

    if (widget.pinned && widget.pinned.length !== 0) {
      widget.pinned.forEach((item) => {
        if (item.objectPath === object.objectPath) {
          objectMatch = true;
        }
        if (item.investorObjectId === widget.investorObjectId) {
          investorMatch = true;
        }
        if (item.widgetId === widget.id) {
          widgetMatch = true;
        }
        if (investorMatch && objectMatch && widgetMatch)
          return true;
        else {
          objectMatch = false;
          investorMatch = false;
          widgetMatch = false;
        }
      });
    }
    return investorMatch && objectMatch && widgetMatch;
  };

  useEffect(() => {
    if (widgetCurrencies) {
      setCurrencies(widgetCurrencies);
    }
  }, [widgetCurrencies]);

  useEffect(() => {
    setWidgetExpand && setWidgetExpand(expand);
  }, [expand]);

  useEffect(() => {
    isPinned(widget, selectedObject) ? setPinned(true) : setPinned(false);
  }, [widget, selectedObject]);

  return (
    <div
      className={`widget-wrapper ${expand ? 'expand-widget' : ''} ${
        widget.type === 'grid' || widget.type === 'treelist' || widget.type === 'document' || widget.type === 'summary' || widget.type === 'annualGrid' ? 'grid-widget-wrapper-padding-off' : ''
      }`}
      style={{
        gridColumnEnd: `span ${widgetView === 'thumb' ? 2 : widget.columnSpan}`,
        gridRowEnd: `span ${widgetView === 'thumb' ? 2 : widget.rowSpan}`,
      }}>
      <div className='widget-header'>
        {customisation && (
          <div className='customisation-ribbon'>
            <Icon className='pointer' name='fas fa-chevron-left'
                  onClick={() => moveWidget && moveWidget(homeContent ? homeContent : widget, -1)} />
            <Icon className='pointer' name='fas fa-chevron-right'
                  onClick={() => moveWidget && moveWidget(homeContent ? homeContent : widget, +1)} />
            {removeWidget && <Icon className='pointer' name='fas fa-times-circle'
                                   onClick={() => removeWidget && removeWidget(homeContent ? homeContent : widget)} />}
          </div>
        )}
        <div className='title-wrapper'>
          {homeContent ? (
            <Link className='title-span link' to={`/boards/${widget.investorObjectId}/${widget.contentId}`}
                  state={{ data: selectedObject.objectPath }}>
              {widget.name}
            </Link>
          ) : (
            <span className='title-span'>{widget.name}</span>
          )}
          <div className='currency-wrapper'>
            {currencySelection && currencies && currencies.length > 1 ? (
              <DropDownList
                data={currencies}
                value={localCurrency}
                className='custom-dropdown-style'
                style={{ marginLeft: 10 }}
                onChange={handleLocalCurrencyChange}
                textField={'currencyShortName'}
                dataItemKey={'currency'}
              />
            ) : (
              localCurrency && <span className='currency-span'>
                {localCurrency?.currencySymbol}
                {localCurrency?.currencyShortName}
              </span>
            )}
          </div>
          {widget.type === 'summary' &&
            localCurrency?.currency === '_' &&
            <p className='local-currency-span'>{selectedCurrency}</p>
          }
          {notes && notes.length > 0 && <NotesPopup notes={notes} />}
        </div>
        <div className='widget-icons'>
          {widget.type !== 'map' && renderPinIcon()}
          {renderIcons}
          {handleLink &&
            <Icon onClick={handleLink} name='far fa-arrow-up-right-from-square' className='widget-icon pointer' />}
          {expand ? (
            <Icon onClick={onCollapse} name='far fa-compress' className='widget-icon pointer' />
          ) : (
            <Icon onClick={onExpand} name='far fa-expand' className='widget-icon pointer' />
          )}
        </div>
      </div>
      <div className='widget-content'>{children}</div>
    </div>
  );
};

export default WidgetWrapper;